/**
 * Pure retailer-name resolution, kept apart from chargeMappingService.ts on
 * purpose: this file is imported by a ts-node seed script
 * (server/features/billValidation/scripts/loadChargeMap.ts), and
 * chargeMappingService.ts pulls in the Vite-only frontend Supabase client
 * (import.meta.env). ts-node's CJS loader cannot load that ES module, so
 * anything the seed script needs must live somewhere that imports neither
 * foundation/ nor Supabase. This module must stay that way.
 */

// A retailer row as far as resolution cares. extn may hold retailer_name_aliases.
export type RetailerRow = { id: string; name: string; extn?: Record<string, unknown> | null };

export type ResolveResult = {
  status: 'exact' | 'single' | 'ambiguous' | 'none';
  retailer?: RetailerRow;
  /** Every retailer row with the same approved name as `retailer`, itself
   *  included. Present whenever `retailer` is. */
  group?: RetailerRow[];
  candidates?: RetailerRow[];
};

/** Name key -> approved System Name (utils/nameAliases.ts, kind 'retailer').
 *  Structural so this file keeps importing nothing. */
export type RetailerNameAliases = ReadonlyMap<string, string>;

// Normalise for exact matching: lowercase, strip everything non-alphanumeric.
// So "AGL Energy", "agl-energy" and "AGLEnergy" all collapse to the same key.
export const normKey = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');

// First word, lowercased — the grouping key for fuzzy candidate matching.
const firstWord = (v: string) => v.trim().toLowerCase().split(/\s+/)[0] ?? '';

// Saved normalised name aliases on a retailer row (extn.retailer_name_aliases).
const aliasesOf = (r: RetailerRow): string[] =>
  ((r.extn as { retailer_name_aliases?: unknown } | null)?.retailer_name_aliases as string[] | undefined) ?? [];

// The approved name a spelling stands for, as a key. Unknown spellings key as themselves.
const groupKey = (name: string, aliases?: RetailerNameAliases): string => {
  const k = normKey(name);
  return normKey(aliases?.get(k) ?? k);
};

/**
 * Every retailer row sharing an approved name with `name` - "AGL" and
 * "AGL Energy" are two rows of one retailer once both resolve to System Name
 * "AGL" (migration 138). Charge map rows hang off whichever row was used when
 * they were loaded, so a reader must take the whole group.
 */
export function retailerGroup(
  name: string,
  retailers: RetailerRow[],
  aliases?: RetailerNameAliases,
): RetailerRow[] {
  const key = groupKey(name, aliases);
  return key ? retailers.filter((r) => groupKey(r.name, aliases) === key) : [];
}

/**
 * Add a normalised name alias to a retailer's extn, preserving every other key.
 * Pure so the merge is unit-testable; the store op just writes what this returns.
 */
export function addRetailerNameAlias(
  extn: Record<string, unknown> | null | undefined,
  aliasName: string,
): Record<string, unknown> {
  const key = normKey(aliasName);
  const existing = ((extn?.retailer_name_aliases as string[] | undefined) ?? []);
  const retailer_name_aliases = existing.includes(key) ? existing : [...existing, key];
  return { ...(extn ?? {}), retailer_name_aliases };
}

/**
 * Resolve a free-text retailer name against the org's retailer rows.
 *  - exact normalised match wins outright ('exact')
 *  - else a saved alias on a retailer resolves outright ('exact')
 *  - otherwise candidates share the first word of the name
 *  - exactly one candidate => 'single' (auto-resolve)
 *  - several => 'ambiguous' (human must choose)
 *  - none => 'none'
 * Pure: no I/O, so it is unit-testable and reused by list/create/remove.
 */
export function resolveRetailer(
  name: string,
  retailers: RetailerRow[],
  aliases?: RetailerNameAliases,
): ResolveResult {
  const found = (status: 'exact' | 'single', retailer: RetailerRow): ResolveResult =>
    ({ status, retailer, group: retailerGroup(retailer.name, retailers, aliases) });

  const target = normKey(name);
  if (target) {
    const exact = retailers.find((r) => normKey(r.name) === target);
    if (exact) return found('exact', exact);

    // A previously chosen alias resolves outright. Checked AFTER exact so an alias
    // can never shadow another retailer's real name.
    const aliased = retailers.find((r) => aliasesOf(r).includes(target));
    if (aliased) return found('exact', aliased);

    // An approved-name spelling ("AGL Sales" -> "AGL") resolves outright too.
    const approved = retailerGroup(name, retailers, aliases)[0];
    if (approved) return found('exact', approved);
  }

  const fw = firstWord(name);
  const candidates = fw ? retailers.filter((r) => firstWord(r.name) === fw) : [];
  if (candidates.length === 1) return found('single', candidates[0]);
  // Several rows of one approved retailer are one retailer, not a choice.
  if (candidates.length > 1 && new Set(candidates.map((r) => groupKey(r.name, aliases))).size === 1) {
    return found('single', candidates[0]);
  }
  if (candidates.length > 1) return { status: 'ambiguous', candidates };
  return { status: 'none' };
}
